import { CompanyAccess, GTMDataLayer, GTMUserInfo, UserDetailsResponse } from "../adapters/types";
import _, { get, has } from 'lodash';
import { useInitializeGoogleTagManager } from "../hooks/useInitializeGoogleTagManager";
import { useAppState } from "../store";
import { userDetails } from "../common/userDetails";

const getEmptyForNull = (value: string | null | undefined): string =>
  value ?? '';

export default getEmptyForNull;
interface StoreInfo {
  label: string;
  value: string;
  storeId: string;
  department: string;
  isDefaultStore: boolean;
}

export const getClientUserInfo = (
  userInfo: UserDetailsResponse | undefined,
  selectedStore: any | undefined
): GTMUserInfo => {
  const findStoreFromList = userInfo?.extendedAccountInfo?.find(
    extend => extend.storeId === selectedStore?.groupName?.storeId
  );
  return {
    userName: userInfo?.iamUser?.username,
    enterpriseId: userInfo?.iamUser?.enterprise.id,
    userRoles: userInfo?.iamUser?.assignments[0]?.roles.map(role => role.code),
    companyId: findStoreFromList?.storeId,
    companyName: selectedStore?.groupName?.label,
    companyAccess:
    userInfo?.extendedAccountInfo?.length &&
    userInfo?.extendedAccountInfo?.length > 1
        ? 'multiple'
        : 'singular',
    departmentId: findStoreFromList?.departmentIds?.[0]
  };
};
export const createDataLayerUpdates = (
  localDataLayer: GTMDataLayer,
  userInfo: GTMUserInfo
): Partial<GTMDataLayer> => {
  const updates: Partial<GTMDataLayer> = {};
 
  if (userInfo.userName && localDataLayer.d_user_id !== userInfo.userName) {
    updates.d_user_id = userInfo.userName;
  }
  if (
    userInfo.enterpriseId &&
    localDataLayer.d_enterprise_id !== userInfo.enterpriseId
  ) {
    updates.d_enterprise_id = userInfo.enterpriseId;
  }
  if (
    userInfo.companyId &&
    localDataLayer.d_company_id !== userInfo.companyId
  ) {
    updates.d_company_id = userInfo.companyId;
  }
  if (
    userInfo.companyName &&
    localDataLayer.d_company_name !== userInfo.companyName
  ) {
    updates.d_company_name = userInfo.companyName;
  }
  if (
    userInfo.userRoles &&
    !_.isEqual(localDataLayer.d_user_roles, userInfo.userRoles)
  ) {
    updates.d_user_roles = userInfo.userRoles;
  }
  if (
    userInfo.companyAccess &&
    localDataLayer.d_company_access !== userInfo.companyAccess
  ) {
    updates.d_company_access = userInfo.companyAccess as CompanyAccess;
  }
  if (
    userInfo.departmentId &&
    !_.isEqual(localDataLayer.d_department_id, userInfo.departmentId)
  ) {
    updates.d_department_id = userInfo.departmentId;
  }

  return updates;
};

export function createAccountObjects(userInfo: any): StoreInfo {
  const accountObjects: any = []; 
  const defaultStores = userInfo.iamUser.assignments?.[0].stores
    .filter((store: any) => store?.defaultStore)
    .map((store: any) => store?.storeId);

  userInfo.extendedAccountInfo.forEach((storeInfo: any) => {
    const isDefaultStore = defaultStores.includes(storeInfo.storeId);
    storeInfo?.accounts?.forEach((account: any) => {
      storeInfo.departmentIds?.forEach((departmentId: any) => {
        accountObjects.push({
          label: storeInfo.storeId + ', ' + account,
          value: account,
          storeId: storeInfo.storeId,
          department: departmentId,
          isDefaultStore: isDefaultStore
        });
      });
    });
  });
  return accountObjects;
}

export function getDefaultStore(userInfo: any): any {
  let result: any = {
    label: '',
    value: '',
    storeId: '',
    department: '',
    isDefaultStore: false
  };

  // Get the list of default stores
  const defaultStores = userInfo.iamUser.assignments?.[0].stores.filter(
    (store: any) => store.defaultStore
  );

  // console.log('defaultStores', defaultStores);

  userInfo?.extendedAccountInfo?.forEach((storeInfo: any) => {
    const isDefaultStore = defaultStores?.[0]?.storeId === storeInfo.storeId;
    const hasAccount = !!storeInfo.accounts?.[0];
    const hasDepartment = !!storeInfo.departmentIds?.[0];
    if (defaultStores && hasAccount && hasDepartment) {
      result = {
        label: storeInfo.storeId + ', ' + storeInfo.accounts[0],
        value:  storeInfo.accounts?.[0],
        storeId: storeInfo.storeId,
        department: storeInfo.departmentIds[0],
        isDefaultStore: true
      };
    }
  });

  return result;
}

interface OrderPayload {
  orderNumber: string;
  orderDate: string;
  orderType: {
    label: string;
    value: string;
  };
  isExcludePastOrders: boolean;
}

interface SelectedGroup {
  groupName: {
    label: string;
    value: string;
    storeId: string;
    department: string;
    isDefaultStore: boolean;
  };
}

export const validateOrder = (
  selectedGroupName: SelectedGroup,
  orderPayload: OrderPayload
): string => {
  if (selectedGroupName.groupName.value === '') {
    return 'Group name is required. Please provide a valid group name.';
  }

  // Check if orderNumber, orderDate, and orderType.value are all blank
  if (
    !orderPayload.orderNumber &&
    !orderPayload.orderDate &&
    !orderPayload.orderType.value
  ) {
    return 'Order number, order date, and order type cannot all be blank. Please ensure at least one of these fields is filled.';
  }

  // If orderDate is present, then orderType.value must also be present
  if (orderPayload.orderDate && !orderPayload.orderType.value) {
    return 'Order type is required when an order date is provided. Please select an order type..';
  }

  // If orderType.value is present, then orderDate must also be present
  if (orderPayload.orderType.value && !orderPayload.orderDate) {
    return 'Order date is required when an order type is selected. Please enter an order date.';
  }
  return '';
};

type Order = any;

export const transformOrders = (orders: Order[]) => {
  const transformedOrders: any[] = [];
  const invalidPartsList: { orderNumber: string; invalidParts: string[] }[] =
    [];

  for (const order of orders) {
    // Filter valid parts before transforming the order
    // const validParts = order.partsEipResponseDTOS.filter(
    //   (part: any) => part && part.isPartValid
    // );
    const validParts = order.partsEipResponseDTOS;

    // If there are no valid parts, skip this order
    if (validParts.length === 0) {
      continue;
    }

    const transformedOrder = {
      purchaseOrderNumber: order.orderNumber,
      orderDate: order.orderDate,
      orderType: order.DTNAOrderType?.value,
      dmsOrderType: order.orderType,
      source: '',
      salesOrg: '',
      distChannel: order.DTNAOrderType?.distChannel,
      customerPO: order.customerPO || order.orderNumber,
      shippingCond: order.shippingCond?.value,
      orderRefText: order.orderRefText || '',
      partsDTO: []
    };

    // Transform only the valid parts
    const partsDTO = validParts.map((part: any) => {
      return {
        dtnaCustomerName:part.dtnaCustomerName,
        customerName:part.customerName,
        partNumber: part.formattedPartNumber === "Invalid Part" ? part.partNumber : part.formattedPartNumber,
        dmsPartNumber: part.partNumber,
        description: part.partDesc,
        quantity: part.orderQty,
        invoiceNumber: part.invoiceNumber,
        price: part.price,
        specialOrderNo: part.specialOrderNo,
        serialNumber: part.serialNumber || '',
        vin: part.vin || '',
        pdc: part.pdc || '',
        vdiFlag: part.vdiFlag || '',
        ...(part.dtnaCustomerName === "OTHERS" && {
          dtnaOtherCustomerName: part.dtnaOtherCustomerName
       }),
       binArray: {
        idx: part.binArray?.idx ?? '',          
        value: part.binArray?.value ?? ''       
      }
        };
    });

    transformedOrder.partsDTO = partsDTO;
    transformedOrders.push(transformedOrder);

    // Find invalid parts for the current order
    const invalidParts = order.partsEipResponseDTOS
      .filter((part: any) => part && !part.isPartValid)
      .map((part: any) => part.partNumber);

    if (invalidParts.length > 0) {
      invalidPartsList.push({
        orderNumber: order.orderNumber,
        invalidParts
      });
    }
  }

  // Attach user data to the transformed orders
  const result = {
    partsOrders: transformedOrders
  };

  return { transformedOrders: result, invalidParts: invalidPartsList };
};

export function validateOrders(orders: any): boolean {
  for (const order of orders) {
    const { DTNAOrderType, customerPO,shippingCond } = order;
    if (DTNAOrderType.value == null || DTNAOrderType.value === '' ||  shippingCond.value == null || shippingCond.value === '') {
      return true;
    }
  }
  return false;
}

export const getInvalidOrders = (orders: any) => {
  return orders
    .filter((order: any) => {
      const { DTNAOrderType, partsEipResponseDTOS } = order;
      const orderType = DTNAOrderType.value;
      return partsEipResponseDTOS.some((part: any) => {
        const { vdiFlag } = part;

        // For ZFSO, ZFDS - no vdiFlag values allowed
        if (['ZFSO', 'ZFDS'].includes(orderType)) {
          return vdiFlag;
        }

        // For ZFWZ, ZFCR - vdiFlag can be optional but allowed if it's 'X'
        if (['ZFWZ', 'ZFCR'].includes(orderType)) {
          return vdiFlag && vdiFlag !== 'X';
        }

        return false;
      });
    })
    .map((order: any) => order.orderNumber);
};

export function areAllPartsInvalid(orders : any) {
    // Iterate over each order and check parts validity
    for (const order of orders) {
      const allPartsInvalid = order.partsEipResponseDTOS.every((part: any) => !part.isPartValid);
      
      // If at least one order has a valid part, return false
      if (!allPartsInvalid) {
        return false;
      }
    }
    // If all orders have invalid parts, return true
    return true;
}